import React, { useState, useEffect } from 'react';
import Modal from '../../../components/common/Modal';
import InputError from '../../../components/common/InputError';
import { 
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { appVersionService } from '../services/appVersionService';

const AppVersionModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  platform, 
  version, 
  isLoading 
}) => {
  const [formData, setFormData] = useState({
    latest_version: '',
    min_version: '',
    force_update: false,
    message: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (version) {
      setFormData({
        latest_version: version.latest_version || '',
        min_version: version.min_version || '',
        force_update: version.force_update || false,
        message: version.message || '',
        is_active: version.is_active !== undefined ? version.is_active : true
      });
    } else {
      setFormData({
        latest_version: '',
        min_version: '',
        force_update: false,
        message: '',
        is_active: true
      });
    }
    setErrors({});
  }, [version, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Validation de la version actuelle
    if (!formData.latest_version) {
      newErrors.latest_version = 'La version actuelle est requise';
    } else if (!appVersionService.validateVersionFormat(formData.latest_version)) {
      newErrors.latest_version = 'Format invalide (ex: 1.0.0)';
    }

    // Validation de la version minimale
    if (!formData.min_version) {
      newErrors.min_version = 'La version minimale est requise';
    } else if (!appVersionService.validateVersionFormat(formData.min_version)) {
      newErrors.min_version = 'Format invalide (ex: 1.0.0)';
    }

    // Vérifier que la version minimale n'est pas supérieure à la version actuelle
    if (
      formData.latest_version && 
      formData.min_version &&
      appVersionService.validateVersionFormat(formData.latest_version) &&
      appVersionService.validateVersionFormat(formData.min_version)
    ) {
      if (appVersionService.compareVersions(formData.min_version, formData.latest_version) > 0) {
        newErrors.min_version = 'La version minimale ne peut pas être supérieure à la version actuelle';
      }
    }

    // Validation du message (optionnel mais avec limite)
    if (formData.message && formData.message.length > 500) {
      newErrors.message = 'Le message ne peut pas dépasser 500 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Nettoyer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getPlatformIcon = () => {
    return platform === 'ios' ? DevicePhoneMobileIcon : ComputerDesktopIcon;
  };

  const getPlatformLabel = () => {
    return platform === 'ios' ? 'iOS' : 'Android';
  };

  const getPlatformColor = () => {
    return platform === 'ios' ? 'text-gray-600' : 'text-green-600';
  };

  const modalTitle = version 
    ? `Modifier la version ${getPlatformLabel()}`
    : `Nouvelle version ${getPlatformLabel()}`;

  const PlatformIcon = getPlatformIcon();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="space-y-6">
        {/* En-tête avec icône de plateforme */}
        <div className="flex items-center space-x-3 pb-4 border-b">
          <div className={`p-2 rounded-lg ${platform === 'ios' ? 'bg-gray-100' : 'bg-green-100'}`}>
            <PlatformIcon className={`h-6 w-6 ${getPlatformColor()}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{modalTitle}</h3>
            <p className="text-sm text-gray-500">
              Configurez les paramètres de version pour {getPlatformLabel()}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Versions sur la même ligne */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Version actuelle */}
            <div>
              <label className="text-left block text-sm font-medium text-gray-700 mb-2">
                Version actuelle *
              </label>
              <input
                type="text"
                value={formData.latest_version}
                onChange={(e) => handleInputChange('latest_version', e.target.value)}
                placeholder="Ex: 1.0.0"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.latest_version ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <InputError message={errors.latest_version} />
              <p className="text-left text-xs text-gray-500 mt-1">
                Version disponible sur l'App Store ou Play Store
              </p>
            </div>

            {/* Version minimale */}
            <div>
              <label className="text-left block text-sm font-medium text-gray-700 mb-2">
                Version minimale requise *
              </label>
              <input
                type="text"
                value={formData.min_version}
                onChange={(e) => handleInputChange('min_version', e.target.value)}
                placeholder="Ex: 1.0.0"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.min_version ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <InputError message={errors.min_version} />
              <p className="text-left text-xs text-gray-500 mt-1">
                Version minimale supportée par l'application
              </p>
            </div>
          </div>

          {/* Checkboxes sur la même ligne */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Mise à jour forcée */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="force_update"
                checked={formData.force_update}
                onChange={(e) => handleInputChange('force_update', e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="force_update" className="text-left block text-sm font-medium text-gray-700">
                  Forcer la mise à jour
                </label>
                <p className="text-left text-xs text-gray-500 mt-1 flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mr-1" />
                  Les utilisateurs devront obligatoirement mettre à jour l'application
                </p>
              </div>
            </div>

            {/* Statut actif */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="is_active" className="text-left block text-sm font-medium text-gray-700">
                  Version active
                </label>
                <p className="text-left text-xs text-gray-500 mt-1">
                  Désactiver temporairement cette version
                </p>
              </div>
            </div>
          </div>

          {/* Message pour les utilisateurs */}
          <div>
            <label className="text-left block text-sm font-medium text-gray-700 mb-2">
              Message aux utilisateurs
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Ex: Nouvelles fonctionnalités et corrections de bugs..."
              rows={3}
              maxLength={500}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <InputError message={errors.message} />
            <div className="flex justify-between items-center mt-1">
              <p className="text-left text-xs text-gray-500 flex items-center">
                <InformationCircleIcon className="h-4 w-4 mr-1" />
                Message affiché lors de la vérification de version (optionnel)
              </p>
              <span className="text-xs text-gray-400">
                {formData.message.length}/500
              </span>
            </div>
          </div>

         
          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-mdfocus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Enregistrement...' : version ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AppVersionModal;